import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useContext,
  useState,
} from "react";
import type { FirmResponse, UserResponse } from "@/lib/types";

export interface Dictionary<T> {
  [index: string]: T;
}

type ConfigContextProps = {
  config: Dictionary<string> | undefined;
  setConfig: Dispatch<SetStateAction<Dictionary<string> | undefined>>;
  featureFlags:
    | Dictionary<(user?: UserResponse, firm?: FirmResponse) => boolean>
    | undefined;
  setFeatureFlags: Dispatch<
    SetStateAction<
      | Dictionary<(user?: UserResponse, firm?: FirmResponse) => boolean>
      | undefined
    >
  >;
};

type ConfigProviderProps = {
  children: ReactNode;
};

const ConfigContext = createContext<ConfigContextProps | null>(null);

export const ConfigProvider = ({ children }: ConfigProviderProps) => {
  const [config, setConfig] = useState<Dictionary<string> | undefined>();
  const [featureFlags, setFeatureFlags] = useState<
    | Dictionary<(user?: UserResponse, firm?: FirmResponse) => boolean>
    | undefined
  >();

  const ctxValues: ConfigContextProps = {
    config,
    setConfig,
    featureFlags,
    setFeatureFlags,
  };

  return (
    <ConfigContext.Provider value={ctxValues}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfigContext = () => {
  const configCtx = useContext(ConfigContext);

  if (configCtx === null) {
    throw new Error("Config context is null");
  }

  return configCtx;
};

export default ConfigContext;
